// @ts-check
/** @jsx jsx */
/** @jsxFrag React.Fragment */
import {
  Box,
  Button,
  Container,
  EditIcon,
  Flex,
  P,
  Text,
  Title,
} from '@bottlebooks/gatsby-theme-base';
import ProductListRowSmall from '@bottlebooks/gatsby-theme-event/src/components/SiteSearch/ProductListRowSmall';
import useEventData from '@bottlebooks/gatsby-theme-event/src/components/SiteSearch/useEventData';
import useSiteConfig from '@bottlebooks/gatsby-theme-event/src/components/useSiteConfig';
import useLink from '@bottlebooks/gatsby-theme-event/src/useLink';
import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import React from 'react';
import { jsx } from 'theme-ui';
import {
  PersonalPageLayout,
  ExploreAllProductsBanner,
} from '../../../gatsby-plugin-favorites/src';
import { useTastingNotes } from '../useTastingNote';
import TastingNoteButton from './TastingNoteButton';

export default function TastingNotesPage({ locale }) {
  return (
    <PersonalPageLayout locale={locale}>
      <Content />
    </PersonalPageLayout>
  );
}

function Content() {
  const { all, isLoading, error } = useTastingNotes();
  const { tastingNotesEnabled } = useSiteConfig();
  if (!tastingNotesEnabled) return <TastingNotesDisabled />;
  if (isLoading) return <Loading />;
  if (!all?.length) return <Empty />;
  if (error) return null;

  // const sortedNotes = orderBy(tastingNotes, 'addedAt', 'desc');
  return <TastingNotes tastingNotes={all} />;
}

function Loading(rest) {
  return (
    <Container sx={{ paddingY: [3, null, 5] }} {...rest}>
      <Text>Loading…</Text>
    </Container>
  );
}

function Empty(rest) {
  const link = useLink();
  return (
    <Container
      sx={{ textAlign: [null, 'center'], paddingY: [3, null, 5] }}
      {...rest}
    >
      <Title sx={{ marginTop: 5, marginBottom: 4 }}>
        <Trans>No products tasted.</Trans>
      </Title>
      <Text variant="large" sx={{ margin: 'auto' }}>
        <Trans>
          You haven't saved any tasting notes. Start your list by tasting some
          products.
        </Trans>
      </Text>
      <Button variant="primary" to={link.products()} sx={{ marginTop: 4 }}>
        <Trans>Explore all products</Trans>
      </Button>
    </Container>
  );
}

function TastingNotes({ tastingNotes, ...rest }) {
  const data = useEventData();
  return (
    <>
      <Container
        sx={{
          paddingX: [0, 'gutter'],
          paddingTop: [0, 3, 5],
          paddingBottom: 5,
        }}
        {...rest}
      >
        {tastingNotes?.map((tastingNote) => (
          <TastedProduct
            key={tastingNote.productId}
            // @ts-ignore TODO: fix this
            tastingNote={addData(tastingNote, data)}
            sx={{ marginBottom: [0, 3] }}
          />
        ))}
      </Container>
      <ExploreAllProductsBanner />
    </>
  );
}

/**
 *
 * @param {object} props
 * @param {ReturnType<addData>} props.tastingNote
 * @returns
 */
function TastedProduct({ tastingNote, ...rest }) {
  const link = useLink();
  const productLink = link.product({
    productId: tastingNote.productId,
    exhibitorId: tastingNote.product?.exhibitorId,
    producerId: tastingNote.product?.producer?.producerId,
  });
  const { i18n } = useLingui();
  const { getButtonProps, getIconProps } = TastingNoteButton.use({
    product: tastingNote.product,
  });
  if (!tastingNote.product) return null;
  return (
    <Box
      sx={{ border: 1, borderColor: 'border', backgroundColor: 'background' }}
      {...rest}
    >
      <ProductListRowSmall
        product={tastingNote.product}
        to={productLink}
        searchTerm=""
      />
      <TastingNoteButton
        {...getButtonProps({ variant: 'text' })}
        sx={{
          paddingX: 3,
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          transition: 'background',
          ':hover': { backgroundColor: 'light' },
        }}
      >
        <TastingNoteButton.Icon {...getIconProps({ size: 'medium' })} />
        <Box sx={{ paddingLeft: 0, flex: 1 }}>
          {tastingNote.note &&
            tastingNote.note.split('\n\n').map((paragraph, index) => (
              <P key={index}>
                {paragraph?.split('\n').map((line, index) => (
                  <Text as="span" sx={{ display: 'block' }} key={index}>
                    {line}
                  </Text>
                ))}
              </P>
            ))}
          <Text variant="smallest" sx={{ color: 'lighterText' }}>
            {tastingNote.addedAt &&
              i18n.date(tastingNote.addedAt, {
                dateStyle: 'medium',
                timeStyle: 'short',
              })}
          </Text>
        </Box>

        <Flex gap={2} sx={{ color: 'lighterText' }}>
          <EditIcon />
          <Text variant="smallest">
            <Trans>Edit</Trans>
          </Text>
        </Flex>
      </TastingNoteButton>
    </Box>
  );
}

function TastingNotesDisabled() {
  const link = useLink();
  return (
    <Container sx={{ paddingY: [3, null, 5] }}>
      <Box>
        <Title variant="large" sx={{ marginTop: 5, marginBottom: 4 }}>
          <Trans>Not available</Trans>
        </Title>
        <P>
          <Trans>Tasting notes are not enabled on this site.</Trans>
        </P>
        <Button variant="primary" to={link.products()} sx={{ marginTop: 4 }}>
          <Trans>Explore all products</Trans>
        </Button>
      </Box>
    </Container>
  );
}

/**
 *
 * @param {import('../useTastingNote').TastingNote & {productId: string}} tastingNote
 * @param {{products: { productId: string; name: string; exhibitorId?: string; producer?: {producerId: string; name: string; } }[], exhibitors:{ brandId: string }[]}} allData
 */
function addData(tastingNote, { products }) {
  return {
    ...tastingNote,
    product: products.find(
      (product) => product.productId === tastingNote.productId
    ),
  };
}
